import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';

export interface UseTableSearchProps {
	delay?: number;
	onDebouncedSearch: (value: string) => void;
	/** Initial search term (e.g. from persisted tableParams) */
	initialValue?: string;
}

export const useTableSearch = ({
	delay = 300,
	onDebouncedSearch,
	initialValue = '',
}: UseTableSearchProps) => {
	const [searchTerm, setSearchTerm] = useState<string>(initialValue);

	const onSearchRef = useRef(onDebouncedSearch);
	useEffect(() => {
		onSearchRef.current = onDebouncedSearch;
	}, [onDebouncedSearch]);

	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				onSearchRef.current(value);
			}, delay),
		[delay],
	);

	useEffect(() => {
		debouncedSearch(searchTerm);
		return () => {
			debouncedSearch.cancel();
		};
	}, [searchTerm, debouncedSearch]);

	const handleSearchInputChange = (value: string) => {
		setSearchTerm(value);
	};

	const clearSearch = useCallback(() => {
		setSearchTerm('');
	}, []);

	return {
		searchTerm,
		handleSearchInputChange,
		clearSearch,
	};
};
